/**
 * QuestionnairesPage — motore questionari di piattaforma (rotta /questionari).
 *
 * Lista dei questionari con builder (crea/modifica domande come DATI),
 * generazione/gestione link di invio; clic su un questionario = la sua pagina
 * con statistiche e iscritti (`/questionari/:id`). Motore generico:
 * nessuna conoscenza del caso d'uso nel codice, tutto vive nei documenti
 * `platform_questionnaires`.
 *
 * Doc canonico: docs/01-ARCHITECTURE/PLATFORM-CRM-QUESTIONARI-MODEL.md
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizzaQuestionario } from '@shared/questionari';
import {
  Alert, Box, Button, CircularProgress, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, BarChart, EditOutlined, Link as LinkIcon, Refresh,
} from '@mui/icons-material';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { QuestionnaireBuilderDialog } from '../components/platform-crm/QuestionnaireBuilderDialog';
import { QuestionnaireLinksDialog } from '../components/platform-crm/QuestionnaireLinksDialog';
import { ElencoQuestionariCompatto } from '../components/platform-crm/questionario/ElencoQuestionariCompatto';
import { formatDate } from '../components/platform-crm/labels';
import { listPlatformLeads, listQuestionnaires } from '../services/platformCrmService';
import type { PlatformLead, PlatformQuestionnaire } from '../types/platformCrm';

export const QuestionnairesPage: React.FC = () => {
  const navigate = useNavigate();
  // Sul telefono l'elenco diventa a righe a due piani: la tabella a sei colonne costringeva a scorrere di lato.
  const telefono = useMediaQuery(useTheme().breakpoints.down('md'));
  const [questionnaires, setQuestionnaires] = useState<PlatformQuestionnaire[]>([]);
  const [leads, setLeads] = useState<PlatformLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Dialog aperti (uno alla volta): builder, link
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderTarget, setBuilderTarget] = useState<PlatformQuestionnaire | null>(null);
  const [linksTarget, setLinksTarget] = useState<PlatformQuestionnaire | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [questionnairesList, leadsList] = await Promise.all([
        listQuestionnaires(),
        listPlatformLeads(),
      ]);
      setQuestionnaires(questionnairesList);
      setLeads(leadsList);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento questionari');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showSnack = (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity });

  const openBuilder = (target: PlatformQuestionnaire | null) => {
    setBuilderTarget(target);
    setBuilderOpen(true);
  };

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: { xs: '1.6rem', md: '1.95rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: tokens.color.graphite,
              fontVariationSettings: '"WONK" 0, "SOFT" 30',
            }}
          >
            Questionari
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 540 }}>
            Motore questionari per la qualificazione delle aziende: costruisci le domande,
            genera i link di invio (generici o personali) e apri un questionario per
            statistiche e iscritti.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          <Tooltip title="Ricarica">
            <IconButton onClick={load} disabled={loading} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => openBuilder(null)}>
            Nuovo questionario
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* TABELLA */}
      <Paper
        variant="outlined"
        sx={{ borderColor: tokens.color.line, bgcolor: tokens.color.paper, overflow: 'hidden' }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : telefono ? (
          <ElencoQuestionariCompatto
            questionari={questionnaires}
            onApri={(q) => navigate(`/questionari/${q.id}`)}
            onModifica={openBuilder}
            onLink={setLinksTarget}
          />
        ) : (
          <Table size="small" sx={{
            '& td': { py: 1.5 },
            '& tr:last-child td': { borderBottom: 0 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Titolo</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right">Domande</TableCell>
                <TableCell>Contatti</TableCell>
                <TableCell>Creato</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questionnaires.map((q) => (
                <TableRow key={q.id} hover onClick={() => navigate(`/questionari/${q.id}`)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}>
                      {q.titolo}
                    </Typography>
                    {q.descrizione && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: tokens.color.mist,
                          display: 'block',
                          maxWidth: 420,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {q.descrizione}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {q.attivo
                      ? <SoftChip label="Attivo" tone="success" />
                      : <SoftChip label="Disattivo" tone="default" outlined />}
                  </TableCell>
                  <TableCell align="right">
                    <Box component="span" sx={{
                      fontFamily: tokens.font.mono, fontSize: '0.85rem',
                      fontWeight: 600, color: tokens.color.graphite,
                    }}>
                      {q.domande?.length ?? 0}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {normalizzaQuestionario(q).contatti
                      ? <SoftChip label="Chiesti" tone="info" />
                      : <SoftChip label="Non chiesti" tone="default" outlined />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: tokens.color.mist, fontFamily: tokens.font.mono }}>
                      {formatDate(q.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2 }} onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => openBuilder(q)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Genera link / link esistenti">
                        <IconButton
                          size="small"
                          sx={{ color: tokens.color.tierPro.main }}
                          onClick={() => setLinksTarget(q)}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Statistiche e iscritti">
                        <IconButton
                          size="small"
                          sx={{ color: tokens.color.tierBusiness.main }}
                          onClick={() => navigate(`/questionari/${q.id}`)}
                        >
                          <BarChart fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {questionnaires.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography
                      sx={{
                        fontFamily: tokens.font.display,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        color: tokens.color.mist,
                      }}
                    >
                      Nessun questionario: crea il primo con "Nuovo questionario".
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Builder crea/modifica */}
      <QuestionnaireBuilderDialog
        open={builderOpen}
        questionnaire={builderTarget}
        onClose={() => setBuilderOpen(false)}
        onSaved={() => {
          showSnack(builderTarget ? 'Questionario aggiornato' : 'Questionario creato', 'success');
          load();
        }}
      />

      {/* Link di invio */}
      <QuestionnaireLinksDialog
        questionnaire={linksTarget}
        leads={leads}
        onClose={() => setLinksTarget(null)}
        showSnack={showSnack}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};
